import { nextTick, PropType, reactive, ref } from 'vue';
import { ORDER_PROPOSAL_STATUS } from '@/typings/enums/ORDER_PROPOSAL_STATUS';
import BaseSetupService from '@/setup/BaseSetupService';
import useOrderClientProposalService from '@/services/OrderClientProposalService';
import useCandidateService from '@/services/CandidateService';
import OrderEntity from '@/entity/order/OrderEntity';

type Props = SetupProps<typeof OrderClientProposalCardSetupService.props>

export default class OrderClientProposalCardSetupService extends BaseSetupService<Props> {
	static readonly props = {
		validated: Boolean,
		selected: Boolean,
		orderClientProposal: {
			type: Object as PropType<IOrderClientProposalWithUserModel>,
			default: null,
		},
	};

	private orderEntity?: OrderEntity;
	private readonly state = reactive({ skillIndexToCut: undefined as Undef<number> });

	readonly _skills = ref<HTMLElement>();
	readonly _talents = ref<HTMLElement>();

	get order () { return this.orderEntity; }

	get tempWorkerAddress () {
		const stringToReturn = [];
		if (this.props.orderClientProposal.tempWorkerUser?.personalInformationCity) {
			stringToReturn.push(this.props.orderClientProposal.tempWorkerUser?.personalInformationCity);
		}
		if (this.props.orderClientProposal.tempWorkerUser?.personalInformationPostCode) {
			stringToReturn.push(this.props.orderClientProposal.tempWorkerUser?.personalInformationPostCode);
		}
		if (this.props.orderClientProposal.tempWorkerUser?.personalInformationCountry) {
			stringToReturn.push(this.props.orderClientProposal.tempWorkerUser?.personalInformationCountry);
		}

		return stringToReturn.join(', ');
	}

	get skills () {
		const skillsToReturn: Partial<IProfessionalSkillModel>[] = this.state.skillIndexToCut
			? this.props.orderClientProposal.tempWorkerUser?.professionalSkills.slice(0, (this.state.skillIndexToCut)) ?? []
			: this.props.orderClientProposal.tempWorkerUser?.professionalSkills ?? [];
		if (this.state.skillIndexToCut) {
			skillsToReturn.push(
				{ label: `+${(this.props.orderClientProposal.tempWorkerUser?.professionalSkills.length ?? 0)- (this.state.skillIndexToCut)}` });
		}
		return skillsToReturn;
	}

	get proposalStatus () {
		switch (this.props.orderClientProposal.status) {
		case ORDER_PROPOSAL_STATUS.Waiting:
			return {
				label: `Candidat proposé le : ${new Date(this.props.orderClientProposal.createdDate).toLocaleDateString()}`,
				color: 'default' as Orion.Color,
			};
		case ORDER_PROPOSAL_STATUS.Accepted:
			return {
				label: this.props.orderClientProposal.responseDate
					? `Accepté le ${new Date(this.props.orderClientProposal.responseDate).toLocaleDateString()}`
					: 'Acccepté',
				color: 'success' as Orion.Color,
			};
		case ORDER_PROPOSAL_STATUS.Refused:
			return {
				label: this.props.orderClientProposal.responseDate
					? `Refusé le ${new Date(this.props.orderClientProposal.responseDate).toLocaleDateString()}`
					: 'Refusé',
				color: 'danger' as Orion.Color,
			};
		default:
			return null;
		}
	}

	constructor (props?: Props, orderEntity?: OrderEntity) {
		super(props);
		this.orderEntity = orderEntity;
	}

	protected onMounted () {
		this.setOverflowCounter();
	}

	setOverflowCounter () {
		if (!this._skills.value) return;

		const skills = this._skills.value;
		const width = skills.getBoundingClientRect().width;
		const gap = +getComputedStyle(skills).gap.replace('px', '');

		nextTick(() => {
			this.getContainerIndexToCut(width, gap);
		});
	}


	getContainerIndexToCut (width: number, gap: number) {
		let sumOfElementsWidth = 0;
		const container = this._skills;

		[...container.value?.children ?? []].forEach((element, index) => {
			const indexIndicatorWidth = index > 9 ? 35 : 30;
			if ((width - (sumOfElementsWidth + indexIndicatorWidth)) < 0) return;
			sumOfElementsWidth += element.getBoundingClientRect().width + gap;
			if ((width - (sumOfElementsWidth + indexIndicatorWidth)) < 0) {
				this.state.skillIndexToCut = index;
			}
		});
	}

	async acceptCandidateAsync () {
		await useOrderClientProposalService().acceptProposalAsync([this.props.orderClientProposal]);
	}

	async declineCandidateAsync () {
		await useOrderClientProposalService().declineProposalAsync([this.props.orderClientProposal]);
	}

	openCandidateProfile (highlightElement?: string) {
		if (!this.props.orderClientProposal.tempWorkerUser?.id) return;

		useCandidateService().openAsideProfile({
			id: this.props.orderClientProposal.tempWorkerUser.id,
			isValidatedCandidate: this.props.validated,
			orderClientProposal: this.props.orderClientProposal,
			order: this.order,
			highlightElement,
		});
	}
}
